import {
	ApiSpot,
	ApiSpotAsset,
	ApiSpotBookingAsset,
	ApiSpotOpeningHoursAsset,
	ApiSpotPhoneAsset,
	ApiSpotPriceAsset,
	ApiSpotUrlAsset,
} from './apiTypes'
import { ItinerarySpot2 as ItinerarySpot } from '~/models/ItinerarySpot'
import { Marker2 as Marker } from '~/models/Marker'
import { PlaceClassification } from '~/models/Place'

import { IBooking, PaginateSpots, Spot2 as Spot, SpotAssets } from '~/models/Spot'
import { ApiItinerarySpot } from '~/passporter-services/itinerary/apiTypes'
import { ItineraryEntityMapper } from '~/passporter-services/itinerary/entityMapper'
export class SpotEntityMapper {
	static toPaginatedSpots(data: any): PaginateSpots {
		const { next, results } = data
		const spots = this.toSpots(results)
		return { nextPage: next, spots }
	}

	static toSpots(spots: ApiSpot[]): Spot[] {
		return spots.map((spot: ApiSpot) => this.toSpot(spot))
	}

	static toMarker = (marker: any, checkValidate?: (marker: Marker) => boolean) => {
		const _marker = {
			id: marker.id,
			location: marker.location,
			color: marker.metadata?.colors?.[0],
			isReference: marker.metadata?.is_reference,
			icon: marker.icon,
		} as Marker
		_marker.isChecked = (checkValidate && checkValidate(marker)) || false
		return _marker
	}

	static toMarkers = (markers: any[], checkValidate?: (marker: Marker) => boolean) => {
		return markers.map((marker) => this.toMarker(marker, checkValidate))
	}

	static toSpot(spot: ApiSpot): Spot {
		return {
			address: spot.address,
			cover: spot.cover,
			description: spot.description,
			id: spot.id,
			location: spot.location,
			name: spot.name,
			numTravelers: spot.num_travelers,
			isPrivate: spot.state === 'private',
			travelersPictures: spot.travelers_pictures,
			type: this.toType(spot.type),
			ownerId: spot.owner_id,
			parentSpotId: spot.parent_spot_id,
			visited: spot.visited,
			placeId: spot.place_id,
			parentId: spot.parent_id,
			mapMarker: spot.map_marker,
			referenceType: 'spot',
			groups: spot.groups ? ItineraryEntityMapper.parseItineraryGroups(spot.groups) : undefined,
		}
	}

	static toItinerarySpot({
		spot,
		is_reference: isReference,
		groups,
		categories,
	}: ApiItinerarySpot): ItinerarySpot {
		const parsedSpot = this.toSpot(spot)
		const parsedGroups = groups && ItineraryEntityMapper.parseItineraryGroups(groups)
		const parsedCategories =
			categories && ItineraryEntityMapper.parseItineraryCategories(categories)
		return {
			...parsedSpot,
			isReference,
			groups: parsedGroups,
			categories: parsedCategories,
		}
	}

	static toItinerarySpots(itinerarySpots: any[]): ItinerarySpot[] {
		return itinerarySpots.map((itinerarySpot: ApiItinerarySpot) =>
			this.toItinerarySpot(itinerarySpot)
		)
	}

	static toType(type: string): PlaceClassification {
		const spotType: PlaceClassification = spotTypes[type] || 'touristAttraction'
		return spotType
	}

	static toAssets(assets: ApiSpotAsset[]): SpotAssets {
		const priceData = assets.find((asset) => asset.type === 'price') as ApiSpotPriceAsset
		const bookingData = assets.find((asset) => asset.type === 'booking') as ApiSpotBookingAsset
		const phoneData = assets.find((asset) => asset.type === 'phone') as ApiSpotPhoneAsset
		const linkData = assets.find((asset) => asset.type === 'url') as ApiSpotUrlAsset
		const openingHoursData = assets.find(
			(asset) => asset.type === 'open_hours'
		) as ApiSpotOpeningHoursAsset

		return {
			phone: phoneData?.value,
			link: linkData?.value,
			price: this.formatAssetPrice(priceData),
			booking: this.mapBookingAsset(bookingData),
			openingHours: openingHoursData?.value,
		}
	}

	private static mapBookingAsset(bookingData?: ApiSpotBookingAsset): IBooking | undefined {
		const booking = bookingData?.value
		if (!booking) return
		return {
			currency: booking.currency,
			freeCancellation: booking.free_cancellation,
			name: booking.name,
			price: booking.price,
			rate: booking.rate,
			url: booking.url,
		}
	}

	private static formatAssetPrice(priceData?: ApiSpotPriceAsset): string | undefined {
		if (!priceData?.value?.price) return
		return `${priceData.value.price} ${priceData.value.currency}`
	}
}

const spotTypes: Record<string, PlaceClassification> = {
	entertainment: 'culture',
	place_of_worship: 'touristAttraction',
	nature: 'outdoorLocation',
	attraction: 'touristAttraction',
	accommodation: 'accommodation',
	transport: 'transport',
	food: 'food',
	drinks: 'drink',
	art_and_culture: 'culture',
	services: 'service',
	coast: 'outdoorLocation',
	parks: 'outdoorLocation',
	sports: 'service',
}
